import { UPGRADES, upgradePrice } from '../../data/upgrades';
import type { CampaignStats, DayResult, SaveData, UpgradeId } from '../types';

const STORAGE_KEY = 'little-corner-save-v1';

const defaultSave = (): SaveData => ({
  version: 3,
  unlockedDay: 1,
  cafeMoney: 0,
  bestResults: {},
  upgrades: {},
  decorations: { wall: 'teal', frameSlot1: 'botanical-placeholder' },
  gameCompleted: false,
  campaignStats: {
    totalCustomersServed: 0,
    totalCafeMoneyEarned: 0,
    totalTips: 0,
    bestCombo: 0,
    totalStars: 0,
  },
  settings: { master: 0.75, music: 0.35, effects: 0.8 },
});

export class SaveSystem {
  private data: SaveData;

  constructor() {
    this.data = this.read();
  }

  get snapshot(): SaveData {
    return structuredClone(this.data);
  }

  get campaignStats(): CampaignStats {
    return {
      ...this.data.campaignStats,
      upgradesCompleted: Object.values(this.data.upgrades).reduce((sum, level) => sum + level, 0),
    };
  }

  recordResult(result: DayResult): void {
    if (result.success) {
      this.data.unlockedDay = Math.max(this.data.unlockedDay, Math.min(20, result.day + 1));
      this.data.cafeMoney += result.earnings + result.bonus;
      this.data.campaignStats.totalCustomersServed += result.served;
      this.data.campaignStats.totalCafeMoneyEarned += result.earnings + result.bonus;
      this.data.campaignStats.totalTips += result.tips;
      this.data.campaignStats.bestCombo = Math.max(this.data.campaignStats.bestCombo, result.maxCombo);
      if (result.day === 20) this.data.gameCompleted = true;
    }
    const previous = this.data.bestResults[result.day];
    if (!previous || result.stars > previous.stars || result.earnings + result.bonus > previous.earnings + previous.bonus) {
      this.data.bestResults[result.day] = result;
    }
    this.data.campaignStats.totalStars = Object.values(this.data.bestResults).reduce((sum, best) => sum + best.stars, 0);
    this.persist();
  }

  updateSettings(settings: SaveData['settings']): void {
    this.data.settings = settings;
    this.persist();
  }

  addMoney(amount: number): void {
    this.data.cafeMoney += amount;
    this.persist();
  }

  purchaseUpgrade(id: UpgradeId): { ok: boolean; reason?: 'locked' | 'max' | 'money'; price?: number } {
    const definition = UPGRADES.find((upgrade) => upgrade.id === id);
    if (!definition || this.data.unlockedDay < definition.unlockDay) return { ok: false, reason: 'locked' };
    const level = this.data.upgrades[id] ?? 0;
    if (level >= definition.maxLevel) return { ok: false, reason: 'max' };
    const price = upgradePrice(definition, level);
    if (this.data.cafeMoney < price) return { ok: false, reason: 'money', price };
    this.data.cafeMoney -= price;
    this.data.upgrades[id] = level + 1;
    this.persist();
    return { ok: true, price };
  }

  unlockAllDays(): void {
    this.data.unlockedDay = 20;
    this.persist();
  }

  activateCheat(): number {
    const remainingUpgradeCost = UPGRADES.reduce((total, definition) => {
      const currentLevel = this.data.upgrades[definition.id] ?? 0;
      for (let level = currentLevel; level < definition.maxLevel; level += 1) {
        total += upgradePrice(definition, level);
      }
      return total;
    }, 0);
    this.data.unlockedDay = 20;
    this.data.cafeMoney = Math.max(this.data.cafeMoney, remainingUpgradeCost);
    this.persist();
    return remainingUpgradeCost;
  }

  resetUpgrades(): void {
    this.data.upgrades = {};
    this.persist();
  }

  reset(): void {
    this.data = defaultSave();
    this.persist();
  }

  private read(): SaveData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultSave();
      const parsed = JSON.parse(raw) as Omit<Partial<SaveData>, 'version'> & { version?: number };
      if (parsed.version !== 1 && parsed.version !== 2 && parsed.version !== 3) return defaultSave();
      const defaults = defaultSave();
      const bestResults = parsed.bestResults ?? {};
      const derivedStats = Object.values(bestResults).reduce((stats, result) => ({
        totalCustomersServed: stats.totalCustomersServed + result.served,
        totalCafeMoneyEarned: stats.totalCafeMoneyEarned + result.earnings + result.bonus,
        totalTips: stats.totalTips + result.tips,
        bestCombo: Math.max(stats.bestCombo, result.maxCombo),
        totalStars: stats.totalStars + result.stars,
      }), { ...defaults.campaignStats });
      const completedDayFive = Boolean(bestResults[5]?.success);
      const unlockedDay = Math.max(1, Math.min(20, completedDayFive ? Math.max(parsed.unlockedDay ?? 1, 6) : (parsed.unlockedDay ?? 1)));
      return {
        ...defaults,
        ...parsed,
        version: 3,
        unlockedDay,
        bestResults,
        upgrades: parsed.upgrades ?? {},
        decorations: { ...defaults.decorations, ...(parsed.decorations ?? {}) },
        settings: { ...defaults.settings, ...(parsed.settings ?? {}) },
        gameCompleted: parsed.gameCompleted ?? false,
        campaignStats: parsed.campaignStats ? { ...defaults.campaignStats, ...parsed.campaignStats } : derivedStats,
      };
    } catch {
      return defaultSave();
    }
  }

  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
  }
}
